import { axiosPrivate } from "../api/axios";
import { useEffect } from "react";
import useRefreshData from "./useRefreshData";
import useAuth from "./useAuth";

const SIGNPOSTING_CONVERSATION_STORAGE_KEY = "signpostingConversationId";

const getStoredConversationId = () => {
  const storedConversationId = window.sessionStorage.getItem(
    SIGNPOSTING_CONVERSATION_STORAGE_KEY,
  );
  const parsedConversationId = Number.parseInt(storedConversationId ?? "", 10);

  if (!Number.isNaN(parsedConversationId)) {
    return parsedConversationId;
  }

  const conversationIdParam = new URLSearchParams(window.location.search).get(
    "conversationId",
  );

  return Number.parseInt(conversationIdParam ?? "", 10);
};

const appendSignpostingConversationId = (config) => {
  if (typeof window === "undefined") {
    return config;
  }

  const requestUrl = config?.url ?? "";
  const isEnquiryTaskRequest = /\/tasks\/create-from-enquiry\/?$/.test(
    requestUrl,
  );
  const isServicePage = window.location.pathname.startsWith("/book-service/");

  if (!isEnquiryTaskRequest || !isServicePage) {
    return config;
  }

  const conversationId = getStoredConversationId();

  if (Number.isNaN(conversationId)) {
    return config;
  }

  if (typeof config.data === "string") {
    try {
      const parsedData = JSON.parse(config.data);

      if (!parsedData.conversation_id) {
        config.data = JSON.stringify({
          ...parsedData,
          conversation_id: conversationId,
        });
      }
    } catch (error) {
      return config;
    }

    return config;
  }

  if (
    config.data &&
    typeof config.data === "object" &&
    !config.data.conversation_id
  ) {
    config.data = {
      ...config.data,
      conversation_id: conversationId,
    };
  }

  return config;
};

const useAxiosPrivate = () => {
  const refresh = useRefreshData();
  const { auth } = useAuth();
  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        const token = auth?.token ?? localStorage.getItem("token");

        if (!config.headers["Authorization"]) {
          // Try to get token from context state
          let token = auth?.token;

          //If state is empty, try to grab it directly from localStorage
          if (!token) {
            try {
              const storedAuth = localStorage.getItem("token");
              token = storedAuth;
            } catch (error) {
              console.error("Error parsing auth from localStorage", error);
            }
          }
          // If we found a token in either place, attach it
          if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
          }
        }

        return appendSignpostingConversationId(config);
      },
      (error) => Promise.reject(error),
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true;
          const { token } = await refresh();
          prevRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosPrivate(prevRequest);
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh]);

  return axiosPrivate;
};

export default useAxiosPrivate;
